import { type CSSProperties, type PointerEvent, type RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { clamp } from "../../math/clamp";
import { clsx } from "../../utils/clsx";
import { Presence } from "../presence/presence";
import { ScrollAreaThumb, getThumbSize } from "./scroll-area-thumb";
import { useHoverWithDelay } from "./use-hover-with-delay";
import { type ScrollSizes, useScrollSizes } from "./use-scroll-sizes";
import { useWheelPassthrough } from "./use-wheel-passthrough";

export type ScrollAreaScrollbarType = "hover" | "overflow" | "hidden";

interface ScrollAreaScrollbarProps {
  className?: string;
  thumbClassName?: string;
  orientation: "vertical" | "horizontal";
  type?: ScrollAreaScrollbarType;
  scrollAreaRef: RefObject<HTMLDivElement | null>;
  viewportRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const ScrollAreaScrollbar = ({
  className,
  thumbClassName,
  orientation,
  type = "hover",
  scrollAreaRef,
  viewportRef,
  contentRef,
}: ScrollAreaScrollbarProps) => {
  const isHorizontal = orientation === "horizontal";

  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  const sizes = useScrollSizes(viewportRef, contentRef, isHorizontal);
  const isOverflowing = useMemo(() => (sizes ? sizes.contentSize > sizes.viewportSize : false), [sizes]);

  const isHovered = useHoverWithDelay(scrollAreaRef);
  const isPassingThrough = useWheelPassthrough(viewportRef, scrollbarRef);

  const rectRef = useRef<DOMRect | null>(null);
  const prevWebkitUserSelectRef = useRef<string>("");
  const pointerOffsetRef = useRef<number | null>(null);

  const updateThumbPosition = useCallback(() => {
    if (!viewportRef.current || !thumbRef.current) {
      return;
    }

    const viewport = viewportRef.current;
    const thumb = thumbRef.current;

    const scrollPos = isHorizontal ? (viewport?.scrollLeft ?? 0) : (viewport?.scrollTop ?? 0);
    const offset = getThumbOffsetFromScroll(scrollPos, sizes);

    thumb.style.transform = isHorizontal ? `translateX(${offset}px)` : `translateY(${offset}px)`;
  }, [viewportRef, sizes, isHorizontal]);

  const onThumbPointerUp = () => {
    pointerOffsetRef.current = null;
  };
  const onThumbPointerDown = (pointerPos: number) => {
    pointerOffsetRef.current = pointerPos;
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    const element = event.target as HTMLElement;
    element.setPointerCapture(event.pointerId);

    rectRef.current = scrollbarRef.current?.getBoundingClientRect() || null;

    prevWebkitUserSelectRef.current = document.body.style.userSelect;
    document.body.style.userSelect = "none";

    if (viewportRef.current) {
      viewportRef.current.style.scrollBehavior = "auto";
    }

    onPointerMove(event);

    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (!viewportRef.current || !rectRef.current) {
      return;
    }

    const viewport = viewportRef.current;

    const pointerPosition = isHorizontal ? event.clientX - rectRef.current.left : event.clientY - rectRef.current.top;
    const scrollPosition = getScrollPositionFromPointer(pointerPosition, pointerOffsetRef.current, sizes);

    if (isHorizontal) {
      viewport.scrollLeft = scrollPosition;
    } else {
      viewport.scrollTop = scrollPosition;
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement;
    if (element.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId);
    }

    document.body.style.userSelect = prevWebkitUserSelectRef.current;

    if (viewportRef.current) {
      viewportRef.current.style.scrollBehavior = "";
    }

    rectRef.current = null;
  };

  useEffect(() => {
    if (!viewportRef.current) {
      return;
    }

    const viewport = viewportRef.current;
    viewport.addEventListener("scroll", updateThumbPosition, { passive: true });

    updateThumbPosition();

    return () => viewport.removeEventListener("scroll", updateThumbPosition);
  }, [updateThumbPosition, viewportRef]);

  const scrollbarVisible = isOverflowing && (isHovered || type === "overflow") && type !== "hidden";

  const scrollbarSizes = {
    "--thumb-size": "var(--spacing)",
    "--thumb-size-hover": "calc(2 * var(--spacing))",
  } as CSSProperties;

  return (
    <Presence
      present={scrollbarVisible}
      className="data-[state=hidden]:animate-scrollbar-hidden data-[state=visible]:animate-scrollbar-visible"
    >
      <div
        ref={scrollbarRef}
        className={clsx(
          "group absolute bg-contrast-50/75 transition-[width,height] duration-100",
          !isHorizontal
            ? "top-0 right-0 h-full w-[var(--thumb-size)] hover:w-[var(--thumb-size-hover)]"
            : "bottom-0 left-0 h-[var(--thumb-size)] w-full hover:h-[var(--thumb-size-hover)]",
          isPassingThrough && "pointer-events-none",
          isPassingThrough && (isHorizontal ? "h-[var(--thumb-size-hover)]" : "w-[var(--thumb-size-hover)]"),
          className,
        )}
        style={scrollbarSizes}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <ScrollAreaThumb
          className={thumbClassName}
          isHorizontal={isHorizontal}
          passThrough={isPassingThrough}
          sizes={sizes}
          onThumbPointerUp={onThumbPointerUp}
          onThumbPointerDown={onThumbPointerDown}
          onThumbPositionChange={updateThumbPosition}
          ref={thumbRef}
          viewportRef={viewportRef}
        />
      </div>
    </Presence>
  );
};

const getThumbOffsetFromScroll = (scrollPos: number, sizes: ScrollSizes) => {
  const thumbSizePx = getThumbSize(sizes);
  const maxScrollPos = sizes.contentSize - sizes.viewportSize;
  const maxThumbPos = sizes.viewportSize - thumbSizePx;

  const clampedScrollPosition = clamp(scrollPos, 0, maxScrollPos);

  return remap(clampedScrollPosition, [0, maxScrollPos], [0, maxThumbPos]);
};

const getScrollPositionFromPointer = (pointerPos: number, pointerOffset: number | null, sizes: ScrollSizes) => {
  const thumbSize = getThumbSize(sizes);
  const thumbCenter = thumbSize / 2;

  const offset = pointerOffset ?? thumbCenter;
  const thumbOffsetFromEnd = thumbSize - offset;

  const minPointerPos = offset;
  const maxPointerPos = sizes.viewportSize - thumbOffsetFromEnd;
  const maxScrollPos = sizes.contentSize - sizes.viewportSize;

  return remap(pointerPos, [minPointerPos, maxPointerPos], [0, maxScrollPos]);
};

const remap = (value: number, [fromMin, fromMax]: [number, number], [toMin, toMax]: [number, number]) => {
  if (fromMin === fromMax || toMin === toMax) {
    return toMin;
  }

  const relativePosition = (value - fromMin) / (fromMax - fromMin);

  return toMin + relativePosition * (toMax - toMin);
};
